import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  X,
  Check,
  AlertCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
} from 'lucide-react';
import { blogAdmin } from '../api';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image'],
    ['blockquote', 'code-block'],
    [{ color: [] }, { background: [] }],
    ['clean'],
  ],
};
const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'link', 'image',
  'blockquote', 'code-block',
  'color', 'background',
];

const PAGE_SIZE = 20;

const BlogAdminPage = () => {
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', slug: '', excerpt: '', mainImageUrl: '', content: '', published: false });

  const loadPosts = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const data = await blogAdmin.listAll(page, PAGE_SIZE);
      setPosts(data.posts || []);
      setPagination(data.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 });
      setError('');
    } catch (err) {
      console.error('Failed to load blog posts:', err);
      setError(err.message || 'Failed to load posts');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPosts(pagination.page);
  }, [pagination.page]);

  const openCreate = () => {
    setEditingPost(null);
    setForm({ title: '', slug: '', excerpt: '', mainImageUrl: '', content: '', published: false });
    setShowModal(true);
  };

  const openEdit = async (post) => {
    setEditingPost(post);
    try {
      const { post: full } = await blogAdmin.getById(post.id);
      setForm({
        title: full.title || '',
        slug: full.slug || '',
        excerpt: full.excerpt || '',
        mainImageUrl: full.mainImageUrl || '',
        content: full.content || '',
        published: full.published ?? false,
      });
      setShowModal(true);
    } catch (err) {
      setError(err.message || 'Failed to load post');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPost(null);
    setForm({ title: '', slug: '', excerpt: '', mainImageUrl: '', content: '', published: false });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      setError('Title and content are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingPost) {
        await blogAdmin.update(editingPost.id, {
          title: form.title.trim(),
          slug: form.slug.trim() || undefined,
          excerpt: form.excerpt.trim() || undefined,
          mainImageUrl: form.mainImageUrl.trim() || undefined,
          content: form.content.trim(),
          published: form.published,
        });
        setSuccess('Post updated');
      } else {
        await blogAdmin.create({
          title: form.title.trim(),
          slug: form.slug.trim() || undefined,
          excerpt: form.excerpt.trim() || undefined,
          mainImageUrl: form.mainImageUrl.trim() || undefined,
          content: form.content.trim(),
          published: form.published,
        });
        setSuccess('Post created');
      }
      closeModal();
      await loadPosts(pagination.page);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    try {
      await blogAdmin.delete(post.id);
      setSuccess('Post deleted');
      await loadPosts(pagination.page);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete post');
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return date.toLocaleDateString(undefined, { dateStyle: 'short' });
  };

  const totalPages = pagination.totalPages || 1;
  const showPagination = pagination.total > PAGE_SIZE;

  return (
    <div className="max-w-5xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8"
      >
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Blog</h1>
          <p className="text-white/60">Manage blog posts (superadmin only).</p>
        </div>
        <button
          onClick={openCreate}
          className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Post
        </button>
      </motion.div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-red-900/20 rounded-xl p-4 border border-red-500/20 flex items-center gap-3 mb-6"
          >
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300">{error}</p>
            <button onClick={() => setError('')} className="ml-auto p-1">
              <X className="w-4 h-4 text-red-400" />
            </button>
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="bg-emerald-900/20 rounded-xl p-4 border border-emerald-500/20 flex items-center gap-3 mb-6"
          >
            <Check className="w-5 h-5 text-emerald-400" />
            <p className="text-emerald-300">{success}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111118] rounded-2xl p-12 border border-white/5 text-center"
        >
          <FileText className="w-16 h-16 text-white/20 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">No posts yet</h2>
          <p className="text-white/60 mb-6">Create your first blog post to get started.</p>
          <button
            onClick={openCreate}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:from-blue-600 hover:to-cyan-600 transition-all"
          >
            Add Your First Post
          </button>
        </motion.div>
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-[#111118] rounded-2xl border border-white/5 overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4 text-white/60 font-medium">Title</th>
                    <th className="text-left py-4 px-4 text-white/60 font-medium">Slug</th>
                    <th className="text-left py-4 px-4 text-white/60 font-medium">Status</th>
                    <th className="text-left py-4 px-4 text-white/60 font-medium">Created</th>
                    <th className="w-24 py-4 px-4 text-right text-white/60 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr key={post.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-3 px-4">
                        <span className="font-medium text-white">{post.title || 'Untitled'}</span>
                      </td>
                      <td className="py-3 px-4 text-white/60 text-sm">{post.slug}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            post.published ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'
                          }`}
                        >
                          {post.published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-white/50 text-sm">{formatDate(post.createdAt)}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEdit(post)}
                            className="p-2 rounded-lg text-white/60 hover:text-blue-400 hover:bg-white/5"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(post)}
                            className="p-2 rounded-lg text-white/60 hover:text-red-400 hover:bg-red-500/10"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {showPagination && (
            <div className="flex items-center justify-between gap-4 mt-6">
              <p className="text-sm text-white/60">
                Showing {(pagination.page - 1) * PAGE_SIZE + 1}–{Math.min(pagination.page * PAGE_SIZE, pagination.total)} of {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                  disabled={pagination.page <= 1}
                  className="p-2.5 rounded-xl bg-white/5 text-white/70 hover:bg-white/10 disabled:opacity-40 disabled:pointer-events-none"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-white/80 font-medium px-2">
                  Page {pagination.page} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPagination((p) => ({ ...p, page: Math.min(totalPages, p.page + 1) }))}
                  disabled={pagination.page >= totalPages}
                  className="p-2.5 rounded-xl bg-white/5 text-white/70 hover:bg-white/10 disabled:opacity-40 disabled:pointer-events-none"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#111118] rounded-2xl border border-white/10 shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="p-6 border-b border-white/10 flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">{editingPost ? 'Edit Post' : 'New Post'}</h2>
                <button onClick={closeModal} className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSave} className="flex flex-col flex-1 min-h-0 overflow-hidden">
                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Title *</label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      placeholder="Post title"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Slug (optional, auto from title)</label>
                    <input
                      type="text"
                      value={form.slug}
                      onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      placeholder="url-slug"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Excerpt (optional)</label>
                    <input
                      type="text"
                      value={form.excerpt}
                      onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      placeholder="Short summary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1 flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" />
                      Main image URL (optional)
                    </label>
                    <input
                      type="url"
                      value={form.mainImageUrl}
                      onChange={(e) => setForm((f) => ({ ...f, mainImageUrl: e.target.value }))}
                      className="w-full px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      placeholder="https://example.com/hero-image.jpg"
                    />
                    {form.mainImageUrl && (
                      <div className="mt-2 rounded-lg overflow-hidden border border-white/10 max-w-xs">
                        <img src={form.mainImageUrl} alt="Preview" className="w-full h-24 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Content * (WYSIWYG)</label>
                    <div className="rounded-xl border border-white/10 overflow-hidden [&_.ql-toolbar]:bg-white/5 [&_.ql-toolbar_.ql-stroke]:border-white/20 [&_.ql-toolbar_.ql-fill]:fill-white/40 [&_.ql-toolbar_.ql-picker]:text-white/80 [&_.ql-container]:bg-white/5 [&_.ql-editor]:text-white [&_.ql-editor.ql-blank::before]:text-white/40 min-h-[200px]">
                      <ReactQuill
                        theme="snow"
                        value={form.content}
                        onChange={(value) => setForm((f) => ({ ...f, content: value }))}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Write your post content…"
                        className="blog-editor-quill"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.published}
                      onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
                      className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500/50"
                    />
                    <span className="text-white/80">Published (visible on blog)</span>
                  </label>
                </div>
                <div className="p-6 border-t border-white/10 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 rounded-xl bg-white/5 text-white/80 hover:bg-white/10"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold hover:from-blue-600 hover:to-cyan-600 disabled:opacity-60 flex items-center gap-2"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editingPost ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BlogAdminPage;
